import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LogOut, Eye } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { useLang } from "../i18n";
import Logo from "../components/Logo";
import Seo from "../components/Seo";
import AdminVehicles from "./AdminVehicles";
import AdminParts from "./AdminParts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_BADGE = {
  new: "bg-[#4499F7]/15 text-[#4499F7] border-[#4499F7]/30",
  contacted: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  closed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

function LoginForm() {
  const { login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0D0F16] border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[rgba(68,153,247,0.5)] transition-colors";

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="admin-login-view">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-[#12141D] border border-white/10 rounded-3xl p-10 spotlight-card overflow-hidden"
      >
        <div className="flex justify-center mb-8">
          <Logo className="h-14 w-14" />
        </div>
        <h1 className="font-display text-2xl font-bold text-center text-slate-50">{t("admin.login")}</h1>
        <p className="mt-2 text-sm text-slate-400 text-center">{t("admin.loginSub")}</p>
        <div className="mt-8 space-y-4">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("admin.email")}
            className={inputCls}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("admin.password")}
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#4499F7] text-[#0A0B10] font-semibold rounded-xl px-6 py-3.5 hover:bg-[#6FB1FA] disabled:opacity-60 transition-colors"
          >
            {loading ? t("admin.signingIn") : t("admin.signIn")}
          </button>
        </div>
      </form>
    </div>
  );
}

function EnquiriesPanel() {
  const { t } = useLang();
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const counts = useMemo(
    () => ({
      total: enquiries.length,
      new: enquiries.filter((e) => e.status === "new").length,
      contacted: enquiries.filter((e) => e.status === "contacted").length,
      closed: enquiries.filter((e) => e.status === "closed").length,
    }),
    [enquiries]
  );

  const visible = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);

  const stats = [
    { id: "total", label: t("admin.total"), value: counts.total },
    { id: "new", label: t("admin.new"), value: counts.new },
    { id: "contacted", label: t("admin.contacted"), value: counts.contacted },
    { id: "closed", label: t("admin.closed"), value: counts.closed },
  ];

  return (
    <div data-testid="admin-enquiries-panel">
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {stats.map((s) => (
          <div key={s.id} data-testid={`admin-stat-${s.id}`} className="bg-[#12141D] border border-white/10 rounded-2xl p-6">
            <p className="font-mono-num text-3xl font-bold text-[#4499F7]">{s.value}</p>
            <p className="mt-2 text-sm text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-3" data-testid="admin-status-filters">
        {["all", "new", "contacted", "closed"].map((s) => (
          <button
            key={s}
            data-testid={`admin-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${
              filter === s
                ? "bg-[#4499F7] text-[#0A0B10] border-[#4499F7]"
                : "border-white/15 text-slate-300 hover:text-[#4499F7]"
            }`}
          >
            {t(`admin.${s}`)}
          </button>
        ))}
        <button
          data-testid="admin-export-csv-btn"
          onClick={async () => {
            try {
              const res = await api.get("/enquiries/export", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = "kataco-enquiries.csv";
              a.click();
              URL.revokeObjectURL(url);
            } catch (err) {
              toast.error(formatApiError(err));
            }
          }}
          className="ml-auto flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium border border-[rgba(68,153,247,0.4)] text-[#4499F7] hover:bg-[#4499F7]/10 transition-colors"
        >
          {t("admin.exportCsv")}
        </button>
      </div>

      <div className="mt-8 bg-[#12141D] border border-white/10 rounded-3xl overflow-hidden" data-testid="admin-enquiries-table">
        {visible.length === 0 ? (
          <p className="p-12 text-center text-slate-500" data-testid="admin-enquiries-empty">{t("admin.empty")}</p>
        ) : (
          visible.map((e) => (
            <div
              key={e.id}
              data-testid={`admin-enquiry-row-${e.id}`}
              className="flex flex-col lg:flex-row lg:items-center gap-4 px-6 py-5 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-100">{e.name}</p>
                <p className="text-sm text-slate-400 truncate">
                  {e.email} · {e.phone}
                </p>
              </div>
              <div className="lg:w-56 min-w-0">
                <p className="text-sm text-slate-300 truncate">{e.vehicle_title || "—"}</p>
                <p className="text-xs text-slate-500 font-mono-num">
                  {new Date(e.created_at).toLocaleDateString("ro-RO")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  data-testid={`admin-enquiry-status-${e.id}`}
                  className={`text-[11px] font-mono-num tracking-widest uppercase border rounded-full px-3 py-1 ${STATUS_BADGE[e.status] || STATUS_BADGE.new}`}
                >
                  {t(`admin.${e.status}`)}
                </span>
                <Select value={e.status} onValueChange={(s) => updateStatus(e.id, s)}>
                  <SelectTrigger
                    data-testid={`admin-status-select-${e.id}`}
                    className="w-36 bg-[#0D0F16] border-white/10 rounded-full h-9 text-xs text-slate-200"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141D] border-white/10 text-slate-200">
                    <SelectItem value="new">{t("admin.new")}</SelectItem>
                    <SelectItem value="contacted">{t("admin.contacted")}</SelectItem>
                    <SelectItem value="closed">{t("admin.closed")}</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  data-testid={`admin-view-btn-${e.id}`}
                  onClick={() => setSelected(e)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#4499F7] transition-colors border border-white/10 rounded-full px-4 py-2"
                >
                  <Eye size={13} />
                  {t("admin.open")}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="bg-[#12141D] border-white/10 text-slate-100 max-w-lg" data-testid="admin-enquiry-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-slate-50">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <p className="text-slate-400">
                  {selected.email} · {selected.phone}
                </p>
                {selected.vehicle_title && (
                  <p className="text-[#4499F7] font-medium">{selected.vehicle_title}</p>
                )}
                <div>
                  <p className="text-[11px] font-mono-num tracking-widest uppercase text-slate-500 mb-2">{t("admin.messageCol")}</p>
                  <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                </div>
                <p className="text-xs text-slate-500 font-mono-num">{new Date(selected.created_at).toLocaleString("ro-RO")}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Dashboard() {
  const { logout } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("enquiries");

  return (
    <div data-testid="admin-dashboard" className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="admin-tabs">
            <button
              data-testid="admin-tab-enquiries"
              onClick={() => setTab("enquiries")}
              className={`rounded-full px-6 py-2.5 font-display font-semibold transition-colors ${
                tab === "enquiries" ? "bg-[#4499F7] text-[#0A0B10]" : "text-slate-300 hover:text-[#4499F7] border border-white/15"
              }`}
            >
              {t("admin.tabEnquiries")}
            </button>
            <button
              data-testid="admin-tab-vehicles"
              onClick={() => setTab("vehicles")}
              className={`rounded-full px-6 py-2.5 font-display font-semibold transition-colors ${
                tab === "vehicles" ? "bg-[#4499F7] text-[#0A0B10]" : "text-slate-300 hover:text-[#4499F7] border border-white/15"
              }`}
            >
              {t("admin.tabVehicles")}
            </button>
            <button
              data-testid="admin-tab-parts"
              onClick={() => setTab("parts")}
              className={`rounded-full px-6 py-2.5 font-display font-semibold transition-colors ${
                tab === "parts" ? "bg-[#4499F7] text-[#0A0B10]" : "text-slate-300 hover:text-[#4499F7] border border-white/15"
              }`}
            >
              {t("admin.tabParts")}
            </button>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#4499F7] transition-colors border border-white/10 rounded-full px-5 py-2.5"
          >
            <LogOut size={15} />
            {t("admin.logout")}
          </button>
        </div>

        {tab === "enquiries" ? <EnquiriesPanel /> : tab === "vehicles" ? <AdminVehicles /> : <AdminParts />}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const { t } = useLang();

  if (user === null) {
    return <div className="pt-40 pb-32 text-center text-slate-500" data-testid="admin-loading">{t("common.loading")}</div>;
  }
  return (
    <div data-testid="admin-page" className="pt-20">
      <Seo title="KATACO Admin" noindex />
      {user ? <Dashboard /> : <LoginForm />}
    </div>
  );
}
